// client/src/components/admin/Lookups.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Lookup, LOOKUP_CATEGORIES, LookupCategory } from '../../types/lookup';
import { fetchLookups, addLookup, toggleLookupStatus, updateLookup } from '../../api/lookups';
import { LookupForm } from './Lookup/LookupForm';
import { Table } from './Lookup/Table';

interface LookupsProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const Lookups: React.FC<LookupsProps> = ({ onSuccess, onError }) => {
  const [selectedCategory, setSelectedCategory] = useState<LookupCategory | ''>('');
  const [lookups, setLookups] = useState<Lookup[]>([]);
  const [editingLookup, setEditingLookup] = useState<Lookup | null>(null);
  const [newLookup, setNewLookup] = useState<Partial<Lookup>>({
    value: '',
    displayName: '',
    sessionRate: undefined,
    numberOfSessions: undefined,
    sortOrder: undefined
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadLookups = useCallback(async (category: LookupCategory) => {
    setIsLoading(true);
    try {
      const data = await fetchLookups(category);
      if (Array.isArray(data)) {
        setLookups(data);
      } else {
        console.error('Invalid lookup data format:', data);
        setLookups([]);
        onError('Failed to load lookups: Invalid data format');
      }
    } catch (error) {
      console.error('Error loading lookups:', error);
      setLookups([]);
      onError('Failed to load lookups');
    } finally {
      setIsLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (selectedCategory) {
      loadLookups(selectedCategory);
    }
  }, [selectedCategory, loadLookups]);

  const handleSubmit = async () => {
    if (!selectedCategory || !newLookup.value) return;

    setIsSubmitting(true);
    try {
      const lookup = {
        value: newLookup.value,
        displayName: newLookup.displayName || newLookup.value,
        sessionRate: newLookup.sessionRate,
        numberOfSessions: newLookup.numberOfSessions,
        sortOrder: newLookup.sortOrder,
        category: selectedCategory,
        active: true
      };

      const savedLookup = await addLookup(lookup);
      setLookups([...lookups, savedLookup]);
      setNewLookup({
        value: '',
        displayName: '',
        sessionRate: undefined,
        numberOfSessions: undefined,
        sortOrder: undefined
      });
      onSuccess('Lookup added successfully');
    } catch (error) {
      onError('Failed to add lookup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (lookup: Lookup) => {
    try {
      const updatedLookup = await updateLookup(lookup);
      const updatedLookups = lookups.map(l =>
        l._id === updatedLookup._id ? updatedLookup : l
      );
      setLookups(updatedLookups);
      setEditingLookup(null);
      onSuccess('Lookup updated successfully');
    } catch (error) {
      onError('Failed to update lookup');
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Category Selection */}
      <div className="glass-card-light rounded-lg">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium theme-text-primary">Select Category</h3>
        </div>
        <div className="p-6">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as LookupCategory)}
            className="theme-input w-full p-2 border rounded-lg"
          >
            <option value="">Select a category</option>
            {Object.entries(LOOKUP_CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCategory && (
        <>
          {/* Add New Lookup Form */}
          <div className="glass-card-light rounded-lg">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-medium theme-text-primary">
                {editingLookup ? 'Edit Lookup' : 'Add New Lookup'}
              </h3>
            </div>
            <div className="p-6">
              <LookupForm
                value={editingLookup?.value || newLookup.value || ''}
                displayName={editingLookup?.displayName || newLookup.displayName || ''}
                sessionRate={editingLookup?.sessionRate || newLookup.sessionRate}
                numberOfSessions={editingLookup?.numberOfSessions || newLookup.numberOfSessions}
                sortOrder={editingLookup?.sortOrder || newLookup.sortOrder}
                category={selectedCategory}
                isEditing={!!editingLookup}
                onChange={(field, value) => {
                  if (editingLookup) {
                    setEditingLookup({ ...editingLookup, [field]: value });
                  } else {
                    setNewLookup({ ...newLookup, [field]: value });
                  }
                }}
                onSubmit={() => {
                  if (editingLookup) {
                    handleEdit(editingLookup);
                  } else {
                    handleSubmit();
                  }
                }}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>

          {/* Lookups Table */}
          <div className="glass-card-light rounded-lg">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-medium theme-text-primary">Current Lookups</h3>
            </div>
            <div className="p-6">
              <Table
                data={lookups}
                category={selectedCategory}
                onToggle={async (lookup) => {
                  try {
                    await toggleLookupStatus(lookup.value, !lookup.active);
                    const updatedLookups = lookups.map(l =>
                      l._id === lookup._id ? { ...l, active: !l.active } : l
                    );
                    setLookups(updatedLookups);
                    onSuccess('Lookup status updated successfully');
                  } catch (error) {
                    onError('Failed to update lookup status');
                  }
                }}
                onEdit={async (lookup) => {
                  setEditingLookup(lookup);
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Lookups;